import { useParams } from 'react-router-dom'
import { Badge, Banner } from '@/ui'
import { useShipment, type ManifestItem, type ShipmentDetail } from '../api/store'
import { ExceptionTable } from '../components/ExceptionTable'
import { ConsolePage, DetailColumns, QueryState } from '../components/Layout'
import { PhotoViewer } from '../components/PhotoViewer'
import { Timeline } from '../components/Timeline'
import { Check, Code, Facts, Kg, RefLink, Section, StatTile, Status, Stream, UserName, When } from '../components/bits'
import {
  BAG_TRIP_STATE_LABEL,
  BAG_TRIP_STATE_TONE,
  EXTRA_KIND_LABEL,
  MISSING_REASON_LABEL,
  bagTripState,
  groupTripEvents,
  missingAndExtra,
  recheckDiffG,
  shipmentKindLabel,
  shipmentStatusLabel,
  tripEventTitle,
} from '../model/shipment'
import { humanize } from '../labels'

/** One trip — F5.1 · F5.2: what left, what arrived, what is missing or extra, and every step. */
export function ShipmentDetailPage() {
  const { id } = useParams<{ id: string }>()
  const q = useShipment(id)
  const s = q.data
  return (
    <ConsolePage
      back={{ to: '/c/shipments', label: 'Shipments' }}
      eyebrow="Store"
      title={s ? <Code className="text-[inherit]">{s.code}</Code> : 'Trip'}
      description={s ? `${shipmentKindLabel(s.kind)} · ${s.fromLabel} → ${s.toLabel}` : undefined}
      actions={s ? <Status status={s.status} label={shipmentStatusLabel(s.status)} /> : undefined}
    >
      <QueryState query={q} label="this trip">
        {s && <ShipmentBody s={s} />}
      </QueryState>
    </ConsolePage>
  )
}

function ShipmentBody({ s }: { s: ShipmentDetail }) {
  const allBags = s.lotGroups.flatMap((l) => l.bags)
  const { missing, extra } = missingAndExtra(allBags, s.exceptions)
  const received = s.status === 'RECEIVED' || s.status === 'RECEIVED_SHORT'
  const events = groupTripEvents(s.events)
  return (
    <>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <StatTile label="Bags" value={s.bags} hint={`${s.lots} ${s.lots === 1 ? 'lot' : 'lots'} · ${s.samples} ${s.samples === 1 ? 'sample' : 'samples'}`} />
        <StatTile label="Unloaded" value={received ? s.unloaded : '—'} hint={received ? 'bags and samples' : 'Not received yet'} />
        <StatTile label="Missing" value={missing.length} tone={missing.length > 0 ? 'danger' : 'neutral'} />
        <StatTile label="Extra" value={extra.length} tone={extra.length > 0 ? 'warn' : 'neutral'} hint={`${s.recheck} to re-weigh`} />
      </div>

      {(missing.length > 0 || extra.length > 0) && (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {missing.length > 0 && (
            <Banner tone="error" title={`${missing.length} ${missing.length === 1 ? 'bag' : 'bags'} missing`}>
              <ul className="mt-1 flex flex-col gap-1 text-ink">
                {missing.map((m) => (
                  <li key={m.key} className="flex flex-wrap items-baseline gap-x-2 text-[13.5px]">
                    <Code>{m.tag ?? 'Unknown tag'}</Code>
                    {m.unitId && <RefLink to={`/c/lots/${m.unitId}`}>{m.unitCode ?? 'lot'}</RefLink>}
                    {m.netGateG !== null && <Kg grams={m.netGateG} className="text-muted" />}
                    {m.reason && <span className="text-muted">· {MISSING_REASON_LABEL[m.reason] ?? humanize(m.reason)}</span>}
                    {m.exceptionId && <RefLink to={`/c/exceptions?open=${m.exceptionId}`} className="font-sans">Open exception</RefLink>}
                  </li>
                ))}
              </ul>
            </Banner>
          )}
          {extra.length > 0 && (
            <Banner tone="warn" title={`${extra.length} extra ${extra.length === 1 ? 'bag' : 'bags'} found`}>
              <ul className="mt-1 flex flex-col gap-1 text-ink">
                {extra.map((x) => (
                  <li key={x.key} className="flex flex-wrap items-baseline gap-x-2 text-[13.5px]">
                    <Code>{x.tag ?? 'Unknown tag'}</Code>
                    {x.unitId && <RefLink to={`/c/lots/${x.unitId}`}>{x.unitCode ?? 'lot'}</RefLink>}
                    {x.kind && <span className="text-muted">· {EXTRA_KIND_LABEL[x.kind] ?? humanize(x.kind)}</span>}
                    <RefLink to={`/c/exceptions?open=${x.exceptionId}`} className="font-sans">
                      {x.open ? 'Open exception' : 'Exception'}
                    </RefLink>
                  </li>
                ))}
              </ul>
            </Banner>
          )}
        </div>
      )}

      <DetailColumns
        main={
          <>
            <Section title="Lots and bags" eyebrow="Manifest" bodyClassName="p-0">
              {s.lotGroups.length === 0 ? (
                <p className="px-4 py-3 text-[14px] text-muted">No bags on this trip.</p>
              ) : (
                s.lotGroups.map((l) => (
                  <div key={l.unitId ?? 'none'} className="border-b border-rule/70 last:border-b-0">
                    <div className="flex flex-wrap items-center gap-2 bg-paper-tint/50 px-4 py-2">
                      {l.unitId ? <RefLink to={`/c/lots/${l.unitId}`}>{l.unitCode ?? 'Lot'}</RefLink> : <span className="text-muted">No lot</span>}
                      <Stream stream={l.stream} />
                      {l.unitHold && l.unitHold !== 'NONE' && <Status status={l.unitHold} />}
                      {l.paperDay && <Status status="PAPER" />}
                      <span className="ml-auto font-mono text-[12px] text-muted">{l.bagsOnTrip} bags</span>
                    </div>
                    <BagTable bags={l.bags} />
                  </div>
                ))
              )}
            </Section>
            <Section title="Samples" bodyClassName="p-0">
              {s.sampleItems.length === 0 ? (
                <p className="px-4 py-3 text-[14px] text-muted">No samples on this trip.</p>
              ) : (
                <ul className="divide-y divide-rule/70">
                  {s.sampleItems.map((it) => (
                    <li key={it.itemId} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2.5 text-[14px]">
                      <span className="text-muted">Seal</span>
                      <Code>{it.sealPayload}</Code>
                      {it.unitId && <RefLink to={`/c/lots/${it.unitId}`}>{it.unitCode ?? 'lot'}</RefLink>}
                      <span className="ml-auto">
                        {it.unloadedAt ? (
                          <Status status="RECEIVED" label="Unloaded" />
                        ) : (
                          <Status status={received ? 'VOID' : 'IN_TRANSIT'} label={received ? 'Not unloaded' : 'Loaded'} />
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </Section>
            <Section title="What happened">
              <Timeline
                items={events.map((g) => ({
                  id: g.id,
                  at: g.at,
                  until: g.until,
                  span: g.count > 1 && g.until !== g.at,
                  title: tripEventTitle(g),
                  detail: g.actorId ? <UserName id={g.actorId} /> : undefined,
                  tone:
                    g.bizStep === 'BAG_MISSING'
                      ? 'danger'
                      : g.bizStep === 'SHIPMENT_RECEIVED'
                        ? s.status === 'RECEIVED_SHORT'
                          ? 'warn'
                          : 'ok'
                        : g.bizStep === 'SHIPMENT_DISPATCHED'
                          ? 'info'
                          : 'neutral',
                }))}
                empty="No steps recorded yet."
              />
            </Section>
          </>
        }
        side={
          <>
            <Section title="Trip">
              <Facts
                columns={2}
                items={[
                  { label: 'Vehicle', value: s.vehicleNo, mono: true },
                  { label: 'Challan', value: s.challanNo, mono: true },
                  { label: 'Driver', value: s.driverName },
                  { label: 'Driver phone', value: s.driverPhone, mono: true },
                  {
                    label: 'Vehicle clean',
                    value: <Check ok={s.vehicleClean} label={s.vehicleClean === null ? 'Not answered' : s.vehicleClean ? 'Yes' : 'No'} />,
                  },
                  { label: 'Kind', value: shipmentKindLabel(s.kind) },
                  { label: 'Left', value: <When at={s.dispatchedAt} /> },
                  { label: 'Received', value: <When at={s.receivedAt} /> },
                  { label: 'Received by', value: <UserName id={s.receivedById} /> },
                  { label: 'Created', value: <When at={s.createdAt} /> },
                ]}
              />
            </Section>
            <Section title="Loaded vehicle photo">
              <PhotoViewer photo={s.photo} alt={`Loaded vehicle, trip ${s.code}`} size="inline" />
            </Section>
            <Section title="Exceptions" bodyClassName="p-0">
              <ExceptionTable rows={s.exceptions} />
            </Section>
          </>
        }
      />
    </>
  )
}

function BagTable({ bags }: { bags: ManifestItem[] }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-[14px]">
        <caption className="sr-only">Bags</caption>
        <thead>
          <tr className="text-left font-mono text-[10.5px] font-bold uppercase tracking-label text-muted">
            <th scope="col" className="px-4 py-1.5">Tag</th>
            <th scope="col" className="px-2 py-1.5 text-right">Gate net</th>
            <th scope="col" className="hidden px-2 py-1.5 text-right sm:table-cell">Sack</th>
            <th scope="col" className="px-2 py-1.5 text-right">Re-weigh</th>
            <th scope="col" className="px-4 py-1.5">On this trip</th>
          </tr>
        </thead>
        <tbody>
          {bags.map((b) => {
            const state = bagTripState(b)
            const diff = recheckDiffG(b)
            return (
              <tr key={b.itemId} className="border-t border-rule/50">
                <td className="px-4 py-1.5">
                  <Code>{b.qrPayload}</Code>
                  {b.hold && b.hold !== 'NONE' && (
                    <Badge tone="warn" className="ml-2">
                      Bag on hold
                    </Badge>
                  )}
                </td>
                <td className="px-2 py-1.5 text-right">
                  <Kg grams={b.netGateG} />
                </td>
                <td className="hidden px-2 py-1.5 text-right sm:table-cell">
                  <Kg grams={b.tareG} className="text-muted" />
                </td>
                <td className="px-2 py-1.5 text-right">
                  {b.recheckG !== null ? (
                    <span className="inline-flex flex-col items-end leading-tight">
                      <Kg grams={b.recheckG} />
                      {diff !== null && diff !== 0 && (
                        <span className={diff < 0 ? 'font-mono text-[11.5px] text-danger' : 'font-mono text-[11.5px] text-ok'}>
                          {diff > 0 ? '+' : '−'}
                          <Kg grams={Math.abs(diff)} className="text-[inherit]" />
                        </span>
                      )}
                    </span>
                  ) : b.recheckRequired ? (
                    <span className="text-[12.5px] text-muted">To re-weigh</span>
                  ) : (
                    <span className="text-muted">—</span>
                  )}
                </td>
                <td className="px-4 py-1.5">
                  <span className="flex flex-wrap items-center gap-1">
                    <Status status={BAG_TRIP_STATE_TONE[state]} label={BAG_TRIP_STATE_LABEL[state]} />
                    {state === 'UNLOADED' && b.status === 'VOID' && <Badge tone="danger">Tag voided since</Badge>}
                  </span>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
